import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ActionButton from './ActionButton';
import Cached from '../Icons/Cached';
// After this long without a DONE/FAILED push, offer to skip the conversion.
const SKIP_AFTER_MS = 3 * 60 * 1000;
const RUN_STATUS = {
    DONE: 'DONE',
    FAILED: 'FAILED'
};
const ConvertingStep = ({ convertUrl, statusUrl, deckRef, onDone, onSkip, onChangeFile }) => {
    const { t } = useTranslation();
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [skipAvailable, setSkipAvailable] = useState(false);
    const [checking, setChecking] = useState(false);
    // eslint-disable-next-line no-undef
    const eventSourceRef = useRef(null);
    const runIdRef = useRef(null);
    useEffect(() => {
        let cancelled = false;
        const skipTimer = setTimeout(() => {
            setSkipAvailable(true);
        }, SKIP_AFTER_MS);
        const fail = () => {
            if (cancelled) {
                return;
            }
            setFailed(true);
        };
        const subscribe = (hubUrl, topic) => {
            if (cancelled) {
                return;
            }
            const url = `${hubUrl}?topic=${encodeURIComponent(topic)}`;
            // The hub allows anonymous subscribers, so no credentials needed.
            // eslint-disable-next-line no-undef
            const eventSource = new EventSource(url);
            eventSourceRef.current = eventSource;
            eventSource.onmessage = (event) => {
                const payload = JSON.parse(event.data);
                if (payload.status === RUN_STATUS.DONE && payload.editUrl) {
                    eventSource.close();
                    eventSourceRef.current = null;
                    onDone(payload.editUrl);
                    return;
                }
                if (payload.status === RUN_STATUS.FAILED) {
                    eventSource.close();
                    eventSourceRef.current = null;
                    fail();
                }
            };
            eventSource.onerror = () => {
                // EventSource auto-reconnects on transient drops; only a permanently
                // closed stream is a hard failure.
                // eslint-disable-next-line no-undef
                if (eventSource.readyState === EventSource.CLOSED) {
                    eventSourceRef.current = null;
                    fail();
                }
            };
        };
        axios
            .post(convertUrl, { deckRef }, {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then((response) => {
            if (cancelled) {
                return;
            }
            const { data } = response;
            // Remember the run so a later skip can check whether it finished.
            runIdRef.current = data.runId;
            // Deck already converted - skip Mercure and go straight to ready.
            if (data.status === RUN_STATUS.DONE && data.editUrl) {
                onDone(data.editUrl);
                return;
            }
            if (!data.mercureHubUrl || !data.mercureTopic) {
                fail();
                return;
            }
            subscribe(data.mercureHubUrl, data.mercureTopic);
        })
            .catch(() => {
            fail();
        });
        return () => {
            cancelled = true;
            clearTimeout(skipTimer);
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
        };
    }, [attempt]);
    const handleRetry = () => {
        setFailed(false);
        setSkipAvailable(false);
        setAttempt((value) => {
            return value + 1;
        });
    };
    const handleSkip = () => {
        if (checking) {
            return;
        }
        const runId = runIdRef.current;
        if (!runId) {
            onSkip();
            return;
        }
        setChecking(true);
        axios
            .post(statusUrl, { runId }, {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then((response) => {
            // The run may have finished while the Mercure push was missed - if so,
            // take the user to the created proposal instead of the blank form.
            if (response.data.status === RUN_STATUS.DONE && response.data.editUrl) {
                onDone(response.data.editUrl);
                return;
            }
            onSkip();
        })
            .catch(() => {
            onSkip();
        });
    };
    if (failed) {
        return (React.createElement("section", { className: "mx-auto w-full max-w-3xl rounded-2xl border border-gray-200 bg-white px-6 pb-6 pt-16 text-center shadow-sm md:pb-8" },
            React.createElement("span", { className: "material-icons text-5xl text-red-600" }, "error"),
            React.createElement("h1", { className: "mt-6 text-2xl font-semibold text-gray-800 md:text-3xl" }, t('pitchCreation.failed.title')),
            React.createElement("p", { className: "mx-auto mt-3 max-w-md text-gray-700" }, t('pitchCreation.failed.body')),
            React.createElement("div", { className: "mt-8 flex justify-end gap-3" },
                React.createElement(ActionButton, { variant: "muted", label: t('pitchCreation.failed.changeFile'), onClick: onChangeFile }),
                React.createElement(ActionButton, { label: t('pitchCreation.failed.retry'), onClick: handleRetry }))));
    }
    return (React.createElement("section", { className: `mx-auto w-full max-w-3xl rounded-2xl border border-gray-200 bg-white px-6 pt-16 text-center shadow-sm ${skipAvailable ? 'pb-6 md:pb-8' : 'pb-16'}` },
        React.createElement(Cached, { className: "inline-block h-9 w-9 animate-spin text-blue-600 motion-reduce:animate-none" }),
        React.createElement("h1", { className: "mt-6 text-2xl font-semibold text-gray-800 md:text-3xl" }, t('pitchCreation.converting.title')),
        React.createElement("p", { className: "mx-auto mt-3 max-w-md text-gray-700" }, t('pitchCreation.converting.body')),
        skipAvailable && (React.createElement(React.Fragment, null,
            React.createElement("p", { className: "mx-auto mt-6 max-w-md text-gray-700" }, t('pitchCreation.converting.timeoutBody')),
            React.createElement("div", { className: "mt-8 flex justify-end" },
                React.createElement(ActionButton, { label: t('pitchCreation.converting.skip'), onClick: handleSkip, variant: "muted", disabled: checking }))))));
};
export default ConvertingStep;
